import { Component } from '@theme/component';

/**
 * Converts the shop-currency secret-box threshold into the cart currency.
 * Shopify.currency.rate is the shop-currency → active-currency multiplier.
 */
class CartGiftTiers extends Component {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #applying = false;

  connectedCallback() {
    super.connectedCallback();
    this.#applyCurrency();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#applyCurrency();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Cart section morph updates this element, then calls updatedCallback on the
   * parsed copy rather than the live node. Watch the live node and convert again.
   */
  #watchCartUpdates() {
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => {
      this.#applyCurrency();
    });
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-eligible', 'data-currency-pending', 'data-active-currency', 'data-tier'],
    });
  }

  #applyCurrency() {
    if (this.#applying) return;
    this.#applying = true;
    this.#observer?.disconnect();
    try {
      this.#renderCurrency();
    } finally {
      this.#applying = false;
      if (this.isConnected) this.#watchCartUpdates();
    }
  }

  #renderCurrency() {
    const shopCurrency = this.dataset.shopCurrency;
    const activeCurrency = this.dataset.activeCurrency;
    const rawRate = window.Shopify?.currency?.rate;

    if (!shopCurrency || activeCurrency === shopCurrency || rawRate == null) {
      this.removeAttribute('data-currency-pending');
      return;
    }

    const rate = Number.parseFloat(rawRate);
    const tier = this.#convert(this.getAttribute('data-tier'), rate);
    if (!Number.isFinite(rate) || rate <= 0 || tier <= 0) {
      this.removeAttribute('data-currency-pending');
      return;
    }

    const eligible = Number.parseInt(this.dataset.eligible || '0', 10) || 0;
    const reached = eligible >= tier;
    const remain = Math.max(0, tier - eligible);
    let progress = tier > 0 ? Math.floor((eligible * 100) / tier) : 0;
    if (progress > 100) progress = 100;
    this.style.setProperty('--gift-tier-progress', `${progress}%`);

    const formattedTier = this.#format(tier);
    for (const node of this.querySelectorAll('[data-gift-amount]')) {
      node.textContent = formattedTier;
    }
    const remainNode = this.querySelector('[data-gift-remain]');
    if (remainNode) remainNode.textContent = this.#format(remain);

    const progressBlock = this.querySelector('[data-gift-progress]');
    const unlocked = this.querySelector('[data-gift-unlocked]');
    if (progressBlock) progressBlock.hidden = reached;
    if (unlocked) unlocked.hidden = !reached;
    this.querySelector('.cart-gift-tiers__card')?.classList.toggle('cart-gift-tiers__card--unlocked', reached);

    const bar = this.querySelector('[role="progressbar"]');
    if (bar) {
      bar.setAttribute('aria-valuemax', String(Math.round(tier / 100)));
      bar.setAttribute('aria-valuenow', String(Math.round(eligible / 100)));
      bar.setAttribute('aria-label', `Postęp do progu ${formattedTier}`);
    }

    this.removeAttribute('data-currency-pending');
  }

  /**
   * @param {string | null} shopCents
   * @param {number} rate
   */
  #convert(shopCents, rate) {
    const cents = Number.parseInt(shopCents || '0', 10) || 0;
    return Math.round(cents * rate);
  }

  /**
   * @param {number} cents
   */
  #format(cents) {
    const currency = this.dataset.activeCurrency || 'PLN';
    const locale = this.#currencyLocale(currency);
    const fractionDigits = cents % 100 === 0 ? 0 : 2;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(fractionDigits);
    }
  }

  /**
   * @param {string} currency
   */
  #currencyLocale(currency) {
    const locales = {
      GBP: 'en-GB',
      USD: 'en-US',
      EUR: 'de-DE',
      PLN: 'pl-PL',
      CHF: 'de-CH',
      CAD: 'en-CA',
      AUD: 'en-AU',
      SEK: 'sv-SE',
      NOK: 'nb-NO',
      DKK: 'da-DK',
      CZK: 'cs-CZ',
    };
    return locales[currency] || this.dataset.locale || 'en';
  }
}

if (!customElements.get('cart-gift-tiers')) {
  customElements.define('cart-gift-tiers', CartGiftTiers);
}
